import lodash from 'lodash';

import { DatasetJobStatus } from '$shared/constants/DatasetJobStatus';
import Label from '$shared/constants/Label';
import type { Trigger } from '$shared/constants/Trigger';
import type { IDatasetMatch } from '$shared/interfaces/api/IDatasetMatch';
import type { IDatasetScan } from '$shared/interfaces/api/IDatasetScan';
import { VerificationData } from '$shared/lib/appData/VerificationData';
import BaseModel from '../BaseModel';
import DateString from '../DateString';
import MessageTemplate from '../MessageTemplate';
import type { MonitoringInfo } from '../MonitoringInfo';
import { DatasetMatch } from './DatasetMatch';
import DatasetScanParam from '../DatasetScanParam';
import type { DatasetScanStatusDesc } from './DatasetScanStatusDesc';
import { ScanOption } from './ScanOption';
import { EntityParameterType } from '$shared/constants/EntityParameterType';

export class DatasetScan extends BaseModel<IDatasetScan> {
  declare public id: string;
  declare public started: DateString;
  declare public completed: DateString;
  declare public type: string;
  declare public status: DatasetJobStatus;
  declare public trigger: Trigger;
  declare public providerId: string | undefined;
  declare public facilityId: string | undefined;
  declare public failureReason: MessageTemplate;
  declare public matches: DatasetMatch[];
  declare public ignoredMatches: DatasetMatch[];
  declare public parameters: DatasetScanParam[];
  declare public options: ScanOption;
  declare public statusDescription: DatasetScanStatusDesc;
  declare public monitoringMetadata: MonitoringInfo;
  declare public verifiedAt: DateString | null;

  constructor(data: IDatasetScan) {
    super(data);
  }

  get isLoading(): boolean {
    return [DatasetJobStatus.Working, DatasetJobStatus.Pending].includes(this.status);
  }

  public get isActive(): boolean {
    return this.status === DatasetJobStatus.Active;
  }

  get isFailed(): boolean {
    return this.status === DatasetJobStatus.Failed;
  }

  get isCompleted(): boolean {
    return this.status === DatasetJobStatus.Completed;
  }

  get verifiedOn(): string {
    return this.verifiedAt?.getLocalizedDateAndTime() || '';
  }

  get completedOn(): string {
    return this.completed?.getLocalizedDateAndTime() || '';
  }

  public get inQueue(): boolean {
    return this.isLoading || this.isActive;
  }

  public get verifiedDateRawString(): string {
    return this.verifiedAt?.getRawString() || '';
  }

  public get referenceId(): string {
    return this.options.referenceId;
  }

  public get hasIncorrectUserAction(): boolean {
    return (
      this.matches.length > 1 &&
      this.matches.every(
        (match) => match.userActionNeeded !== this.matches.first().userActionNeeded
      )
    );
  }

  get sourceName(): string {
    return VerificationData.data?.getDatasetSourceName(this.type) || '';
  }

  public override get metadata(): Record<string, any> {
    const meta: Record<string, any> = { source: this.sourceName };
    if (this.isFailed) {
      return { ...meta, completedOn: this.completedOn };
    }
    return { ...meta, verifiedOn: this.verifiedOn };
  }

  public get hasMatches(): boolean {
    return Array.isArray(this.matches) && this.matches.length !== 0;
  }

  public get resultInfoLabel(): string {
    if (this.hasMatches) return '';
    if (this.isFailed) return 'Failed';
    return 'No Match Found';
  }

  public get parsedUIStatus(): string {
    if (this.isLoading) return '';
    if (this.isMatchesReview) return Label.Review;
    if (this.status === DatasetJobStatus.Failed) return Label.Failed;
    if (!this.matches || !this.matches?.first()) return Label.NotFound;
    return Label.Found || '';
  }

  public get hasAnyIgnoreMatchesHaveUserActionNeeded(): boolean {
    return this.ignoredMatches.some((match: DatasetMatch) => match.userActionNeeded);
  }

  public get isMatchesReview(): boolean {
    return !!this.matches?.some((match) => match.isReview);
  }

  public override toProvidedInfo(): IDataList {
    let parameters = this.parameters;
    if (this.matches?.length > 0) {
      parameters = this.getConstructedHighlightingMatchParams();
    }

    return this.iteratedParameters(parameters) || {};
  }

  protected transformResponse(): void {
    this.id = this.response?.id;
    this.started = new DateString(this.response.started);
    this.completed = new DateString(this.response.completed);
    this.type = this.response.type;
    this.status = this.response.status;
    this.trigger = this.response.trigger;
    this.providerId = this.response.providerId;
    this.facilityId = this.response.facilityId;
    this.failureReason = new MessageTemplate(this.response.failureReason);

    // this.statusDescription = this.response.statusDescription
    //   ? new DatasetScanStatusDesc(this.response.statusDescription)
    //   : null;

    // this.monitoringMetadata =
    //   (this.response.monitoringMetadata && MonitoringInfo.from(this.response.monitoringMetadata)) ||
    //   null;

    this.verifiedAt = DateString.create(this.response.verifiedAt);
    this.ignoredMatches = [];
    this.matches = [];

    const scanData = { ...this.response };
    delete scanData.matches;
    this.response?.matches?.forEach((match: IDatasetMatch) => {
      const newMatch = new DatasetMatch({ ...match, scan: scanData });
      if (match.isIgnored) {
        this.ignoredMatches.push(newMatch);
        return;
      }
      this.matches.push(newMatch);
    });
    this.parameters = this.response.parameters.map((param) => new DatasetScanParam(param));
    this.options = ScanOption.fromScanResponse(this.response.options);

    // Store all the name type parameters values to nameTypeParamValues property
    // this.nameTypeParamValues = this.parameters
    //   .filter((param: DatasetScanParam) => param.type === EntityParameterType.ProviderName)
    //   .map((param: DatasetScanParam) => param.value);
  }

  private getConstructedHighlightingMatchParams(): Array<DatasetScanParam> {
    const tempParamList: DatasetScanParam[] = [];
    const parametersCloneList = lodash.clone(this.parameters);

    const positiveParams: DatasetScanParam[] = DatasetMatch.getPositiveParams(this.matches);
    const negativeParams: DatasetScanParam[] = DatasetMatch.getNegativeParams(this.matches);

    const highlightParam = (param: DatasetScanParam, icon: string, showTooltip = false): void => {
      const index = parametersCloneList.findIndex((paramItem) => paramItem.value === param.value);
      if (index === -1) return;

      const parameter = parametersCloneList[index];
      parameter.aliasValue = { actualValue: param.value, icon, showTooltip };
      tempParamList.push(parameter);
      // Remove that param from parametersCloneList,
      // to aviod the duplicates
      parametersCloneList.splice(index, 1);
    };

    positiveParams.forEach((param) => highlightParam(param, 'icon-check')); // For Positive params
    negativeParams.forEach((param) => highlightParam(param, 'icon-alert-triangle', true)); // For Negative params
    parametersCloneList.forEach((param) => tempParamList.push(param)); // For Remaining params

    return tempParamList;
  }

  private iteratedParameters(parameters: DatasetScanParam[], allowRequiredKey = true): IDataList {
    const tempProvidedInfo: IDataList = {};

    parameters?.forEach((param: DatasetScanParam) => {
      const paramValue: any = param.aliasValue || param.value;
      let value: DataListValue;
      if (typeof paramValue === 'string') {
        value = {
          actualValue: paramValue,
          isRequired: allowRequiredKey
        };
      } else {
        paramValue.isRequired = allowRequiredKey;
        value = paramValue;
      }

      if (param.type === EntityParameterType.FacilityName) {
        const isHandled = this.handlePrimaryVsAliasName(param.type, value, tempProvidedInfo);
        if (isHandled) return;
      }

      param.type && (tempProvidedInfo[param.type] = value);
    });

    return tempProvidedInfo;
  }

  private getFirstIndexParameterValue(type: EntityParameterType): string | undefined {
    const param = this.parameters.find((p) => p.type === type);
    return param?.value;
  }

  private handlePrimaryVsAliasName(
    type: EntityParameterType,
    value: DataListValue,
    tempResult: IDataList
  ): boolean {
    const primaryName = this.getFirstIndexParameterValue(type);

    const parsedStringValue: string = value?.actualValue || value;
    if (parsedStringValue !== primaryName || tempResult[type]) {
      tempResult[Label.DbaName] = value;
      return true;
    }
    return false;
  }
}
